import { useSnakeMarketsGetTabs } from '@/lib/endpoints';
import { SnakeTabResponseDtoSection } from '@/lib/models';
import type { SnakeSeriesResponseDto } from '@/lib/models';
import { formatSpot, parsePrice } from '@/lib/snake-market';
import { CollapsibleSection } from '@/components/features/BinaryMarkets/CollapsibleSection';
import { buildSnakeTabs, type SnakeTabKey } from './build-snake-tabs';
import { OutcomeOrderBook } from '../BinaryMarkets/OutcomeOrderBook';
import { SNAKE_SECTION_ROW_DIVIDED, SNAKE_SECTION_HEADER } from './section-row-style';

export interface SnakeSectionsProps {
  /** The current series instance (drives Market Rules / Timeline content). */
  series: SnakeSeriesResponseDto;
}

/** Human title per snake tab key. */
const TAB_TITLE: Record<SnakeTabKey, string> = {
  ORDER_BOOK: 'Order Book',
  MARKET_RULES: 'Market Rules',
  TIMELINE_PAYOUT: 'Timeline & Payout',
  PEOPLE_ALSO_TRADING: 'People Are Also Trading',
  PEOPLE_SAYING: 'People Are Saying',
};

/**
 * Admin-ordered info tabs for the snake market page (ARC-84 §5).
 *
 * Order + default-open come from `useSnakeMarketsGetTabs` (`/snake/config/tabs`,
 * already in admin order). Reuses the binary {@link CollapsibleSection} shell.
 *
 * - **Market Rules** — renders the strike / comparator summary from the series
 *   outcome (the full Plaee outcome-rules blob is not on the player series DTO
 *   yet — see TODO).
 * - **Timeline & Payout** — payout framing ($1/contract) + the resolution
 *   schedule label.
 * - **Order Book / People Are Also Trading / People Are Saying** — gated shells
 *   pending the order-book, trending-by-series, and comments APIs.
 */
export function SnakeSections({ series }: SnakeSectionsProps) {
  const { data: config } = useSnakeMarketsGetTabs();
  const resolved = buildSnakeTabs(config);

  const strike = parsePrice(series.outcome?.strikePrice ?? null);
  const operator = series.outcome?.strikeOperator;
  // Plaee external outcome id — the trading API (orderbook/orders/positions) keys
  // on Plaee's outcome id, not our local cuid.
  const outcomeId = series.outcome?.externalOutcomeId ?? '';

  return (
    // At lg+ the wrapper dissolves (display: contents) so each section becomes a
    // direct row of the parent card that `SnakeMarketPage` renders around all
    // sections, keeping the hairline dividers uniform across components.
    <div className="flex flex-col gap-2.5 lg:contents">
      {resolved.map(({ section, defaultOpen }) => {
        const title = TAB_TITLE[section];

        switch (section) {
          case SnakeTabResponseDtoSection.MARKET_RULES:
            return (
              <CollapsibleSection
                key={section}
                title={title}
                className={SNAKE_SECTION_ROW_DIVIDED}
                headerClassName={SNAKE_SECTION_HEADER}
                defaultOpen={defaultOpen}
              >
                {/* TODO(snake-rules): render the full Plaee outcome-rules blob once
                    the player series DTO carries it. Today we summarise the strike. */}
                <div className="flex flex-col gap-2 text-sm text-primary/80">
                  <p className="m-0">
                    Resolves <span className="font-semibold">Up</span> if {series.instrument} settles{' '}
                    {operator === 'GT' || operator === 'GTE' ? 'at or above' : 'above'} the price to beat of{' '}
                    <span className="font-semibold tabular-nums">{formatSpot(strike)}</span> at close, otherwise{' '}
                    <span className="font-semibold">Down</span>.
                  </p>
                  <p className="m-0 text-muted">Resolution period: {series.resolution.label}.</p>
                </div>
              </CollapsibleSection>
            );

          case SnakeTabResponseDtoSection.TIMELINE_PAYOUT:
            return (
              <CollapsibleSection
                key={section}
                title={title}
                className={SNAKE_SECTION_ROW_DIVIDED}
                headerClassName={SNAKE_SECTION_HEADER}
                defaultOpen={defaultOpen}
              >
                <div className="flex flex-col gap-2 text-sm text-primary/80">
                  <p className="m-0">
                    Each contract pays <span className="font-semibold text-success">$1.00</span> if it resolves correct, or{' '}
                    <span className="font-semibold">$0.00</span> otherwise.
                  </p>
                  <p className="m-0 text-muted">This {series.resolution.label} series settles at its close time.</p>
                </div>
              </CollapsibleSection>
            );

          // Snapshot depth from /plaee/orderbook/:outcomeId (lazy — only fetched
          // when expanded). TODO(snake-orderbook-limit): limit-order entry (the
          // Figma "Limit Order" trailing toggle) once the design lands.
          case SnakeTabResponseDtoSection.ORDER_BOOK:
            return (
              <CollapsibleSection
                key={section}
                title={title}
                className={SNAKE_SECTION_ROW_DIVIDED}
                headerClassName={SNAKE_SECTION_HEADER}
                defaultOpen={defaultOpen}
                disabled={!outcomeId}
              >
                <OutcomeOrderBook
                  outcomeId={outcomeId}
                  marketId={series.marketId}
                  externalMarketId={series.externalMarketId}
                />
              </CollapsibleSection>
            );

          // TODO(snake-trending): reuse the binary trending list once a
          // trending-by-instrument/series endpoint exists (the binary
          // `TrendingContent` keys off an eventId snake series don't carry).
          case SnakeTabResponseDtoSection.PEOPLE_ALSO_TRADING:
            return (
              <CollapsibleSection
                key={section}
                title={title}
                className={SNAKE_SECTION_ROW_DIVIDED}
                headerClassName={SNAKE_SECTION_HEADER}
                defaultOpen={defaultOpen}
              >
                <p className="text-sm text-muted">Related markets are coming soon.</p>
              </CollapsibleSection>
            );

          // TODO(comments): post/read comments. Needs the comments API.
          case SnakeTabResponseDtoSection.PEOPLE_SAYING:
            return (
              <CollapsibleSection
                key={section}
                title={title}
                className={SNAKE_SECTION_ROW_DIVIDED}
                headerClassName={SNAKE_SECTION_HEADER}
                disabled
                summary={<span className="text-muted">Comments coming soon</span>}
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
